/* ===========================================================================
   hc-wishlist.js - HC Wishlist, Phase 1 (owner, 2026-10-09)
   ---------------------------------------------------------------------------
   The storefront half of what the wishlist apps (Swym, Wishlist King,
   Wishlist Hero) do: hearts on product cards, beside Add to cart (product page
   and quick add), a header heart with a count, "Save for later" in the cart,
   more than one list, and wishlist events for Klaviyo / Google / Meta /
   Shopify pixels. The Wishlist page itself is assets/hc-wishlist-page.js.

   Phase 1 keeps the lists in this browser (localStorage). Phase 2 syncs the
   same data to the customer account through an app proxy, so every change
   goes through write() and one 'hc:wishlist:change' event.

   Customers only (owner, 2026-10-09; Theme settings > HC Wishlist > "Only
   signed-in customers can save"): guests see the hearts, and tapping one (or
   Save for later) asks them to sign in. The item waits in localStorage
   ("pending") and is saved when they come back signed in.

   Guest page (Apps > HC Wishlist > Guest page; off until turned on there): the
   header heart opens the store's Wishlist page for guests, a sign-in prompt
   with what they tapped. Every heart a guest taps is then kept on this device
   for a day ("held", owner 2026-10-23: "hold for the day"), shown there, and
   saved once they sign in, wherever they land.

   Account sync (Phase 2, Theme settings > HC Wishlist > "Save lists to
   customer accounts", once the HC Wishlist app is installed): a signed-in
   customer's lists live on their account (the app's $app.wishlist customer
   metafield). Every change is applied here at once and queued as a small
   operation, sent to the app proxy (/apps/wishlist/ops) and kept in
   localStorage until the store confirms it, so nothing is lost to a closed
   tab or a dropped connection. The account copy is read back on page views
   (/apps/wishlist/lists, at most once a minute), which brings in changes made
   on another device or on the account's Wishlist page. The first sync moves
   anything saved on this device into the account ("merge").

   Loaded by snippets/hc-globals.liquid only when Theme settings > HC
   Wishlist is turned on. Safari note: the heart glyph is painted by the
   button through a CSS mask (snippets/hc-wishlist-styles.liquid) - card grids
   run at Safari's layer budget, so nothing here adds per-card layers.
   =========================================================================== */

const VERSION = 1;
const DEFAULT_ID = 'wishlist';
const LATER_ID = 'later';

const config = (() => {
  try {
    return JSON.parse(document.getElementById('hc-wishlist-config')?.textContent || '{}');
  } catch {
    return {};
  }
})();
const PAGE_URL = config.pageUrl || '/pages/wishlist';
const STORE_PAGE_URL = config.storePage || '/pages/wishlist';
const LISTS_ON = config.lists !== false;
const CUSTOMER = config.customer ? String(config.customer) : null;
/* The signed-in shopper's email, for Klaviyo (track(): it only records events for shoppers it can name). */
const CUSTOMER_EMAIL = CUSTOMER && typeof config.customerEmail === 'string' && config.customerEmail.includes('@') ? config.customerEmail : null;
const GUEST_LOCKED = config.customersOnly !== false && !CUSTOMER;
const KEY = CUSTOMER ? `hc-wishlist:c${CUSTOMER}` : 'hc-wishlist';
const PENDING_KEY = 'hc-wishlist-pending';
const PENDING_MAX_AGE = 30 * 60 * 1000;
const GUEST_PAGE = config.guestPage === true;
const HELD_KEY = 'hc-wishlist-held';
const HELD_MAX_AGE = 24 * 60 * 60 * 1000;
const HELD_MAX = 24;
const SYNC = !!(CUSTOMER && config.sync);
const PROXY = config.proxy || '/apps/wishlist';
const QUEUE_KEY = `hc-wishlist-queue:c${CUSTOMER}`;
const FRESH_KEY = `hc-wishlist-fresh:c${CUSTOMER}`;
const FRESH_MS = 60 * 1000;

/* Wording and marketing events from the app's page in Shopify admin (Apps > HC Wishlist,
   passed in by snippets/hc-globals.liquid), with these defaults. */
const WORDS = {
  saved: 'Saved to your wishlist',
  removed: 'Removed from your wishlist',
  removedLists: 'Removed from your lists',
  view: 'View',
  undo: 'Undo',
  changeList: 'Change list',
  signIn: 'Sign in to save items to your wishlist',
  signInLater: 'Sign in to save items for later',
  signInLink: 'Sign in',
  savedLater: 'Saved for later',
  moved: 'Moved to your cart',
};
for (const [key, value] of Object.entries(config.wording || {})) {
  if (key in WORDS && typeof value === 'string' && value.trim()) WORDS[key] = value.trim().slice(0, 120);
}
const MARKETING = { klaviyo: true, ga4: true, meta: true, shopify: true };
for (const [key, value] of Object.entries(config.marketing || {})) {
  if (key in MARKETING && typeof value === 'boolean') MARKETING[key] = value;
}

/* ---------- storage ---------- */

function blank() {
  return { v: VERSION, lists: [{ id: DEFAULT_ID, name: 'Wishlist', items: [] }] };
}

function read() {
  /* A signed-out shopper has no list when saving is for customers only. */
  if (GUEST_LOCKED) return blank();
  let data = null;
  try {
    data = JSON.parse(localStorage.getItem(KEY) || 'null');
  } catch {
    data = null;
  }
  return sanitize(data);
}

function sanitize(data) {
  if (!data || data.v !== VERSION || !Array.isArray(data.lists)) data = blank();
  data.lists = data.lists.filter((l) => l && typeof l.id === 'string');
  if (!data.lists.some((l) => l.id === DEFAULT_ID)) data.lists.unshift({ id: DEFAULT_ID, name: 'Wishlist', items: [] });
  data.lists.forEach((l) => {
    if (!Array.isArray(l.items)) l.items = [];
    l.items = l.items.filter((i) => i && i.p && i.h);
  });
  return data;
}

let state = read();

function emit(reason, detail = {}) {
  document.dispatchEvent(new CustomEvent('hc:wishlist:change', { detail: { reason, ...detail } }));
}

function write(reason, detail) {
  if (GUEST_LOCKED) return;
  state.updated = Date.now();
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private window or full storage: the list still works for this page view */
  }
  emit(reason, detail);
}

/* ---------- account sync ---------- */

function readQueue() {
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(queue) ? queue : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    if (queue.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-200)));
    else localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* without storage the change still goes out with this page's flush */
  }
}

let flushTimer = 0;
let flushing = false;

function queueOp(op) {
  if (!SYNC) return;
  writeQueue([...readQueue(), op]);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 300);
}

/** The account's copy becomes this page's copy (no local changes are waiting). */
function adoptServer(data) {
  if (!data) return;
  state = sanitize(data);
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    localStorage.setItem(FRESH_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  emit('sync');
}

async function flush() {
  if (!SYNC || flushing) return;
  const queue = readQueue();
  if (!queue.length) return;
  const batch = queue.slice(0, 50);
  flushing = true;
  try {
    const res = await fetch(`${PROXY}/ops`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ops: batch }),
      credentials: 'same-origin',
      keepalive: true,
    });
    const body = await res.json().catch(() => null);
    if (res.ok && body?.ok) {
      /* Changes made while this was on its way stay queued and go next. */
      const rest = readQueue().slice(batch.length);
      writeQueue(rest);
      if (rest.length) setTimeout(flush, 0);
      else adoptServer(body.data);
    } else if (res.status === 400 || res.status === 401) {
      /* Malformed, or the store no longer sees a signed-in customer: don't retry forever. */
      writeQueue(readQueue().slice(batch.length));
    }
  } catch {
    /* Offline or the service is down: the queue waits for the next change or page. */
  } finally {
    flushing = false;
  }
}

async function pull(force = false) {
  if (!SYNC) return;
  if (readQueue().length) return flush();
  let fresh = 0;
  try {
    fresh = Number(localStorage.getItem(FRESH_KEY)) || 0;
  } catch {
    fresh = 0;
  }
  if (!force && Date.now() - fresh < FRESH_MS) return;
  try {
    const res = await fetch(`${PROXY}/lists`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.ok) return;
    /* A change made while this was on its way goes out first; its reply brings the lists. */
    if (readQueue().length) return flush();
    if (body.data) adoptServer(body.data);
    else if (state.lists.some((l) => l.items.length)) queueOp({ op: 'merge', data: state });
    else localStorage.setItem(FRESH_KEY, String(Date.now()));
  } catch {
    /* try again on the next page */
  }
}

/* ---------- sign in to save (customers only) ---------- */

function signInUrl() {
  const back = `${location.pathname}${location.search}`;
  return `${config.loginUrl || '/customer_authentication/login'}?return_to=${encodeURIComponent(back)}`;
}

function setPending(pending) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, at: Date.now() }));
  } catch {
    /* without storage the shopper signs in and taps the heart again */
  }
}

/** Returns and clears what a guest asked to save before signing in (only once signed in). */
function takePending(kind) {
  if (GUEST_LOCKED) return null;
  let pending = null;
  try {
    pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
  } catch {
    pending = null;
  }
  if (!pending || (kind && pending.kind !== kind)) return null;
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
  return Date.now() - (pending.at || 0) < PENDING_MAX_AGE ? pending : null;
}

/* ---------- hearts held for a guest (guest page on) ---------- */

/** What a guest tapped in the last day, newest first: [{p, h, v, title, price, at}]. */
function readHeld() {
  let held = [];
  try {
    held = JSON.parse(localStorage.getItem(HELD_KEY) || '[]');
  } catch {
    held = [];
  }
  const now = Date.now();
  return Array.isArray(held) ? held.filter((i) => i && i.p && i.h && now - (Number(i.at) || 0) < HELD_MAX_AGE).slice(0, HELD_MAX) : [];
}

function writeHeld(held) {
  try {
    if (held.length) localStorage.setItem(HELD_KEY, JSON.stringify(held.slice(0, HELD_MAX)));
    else localStorage.removeItem(HELD_KEY);
  } catch {
    /* without storage the shopper signs in and taps the heart again */
  }
  emit('held');
}

/** Keeps a guest's heart until they sign in: once per product and size, a sized one
    replacing the same product saved without a size (as add() does). */
function hold(entry) {
  if (!GUEST_PAGE || !entry?.p || !entry?.h) return;
  const v = entry.v ? String(entry.v) : null;
  const held = readHeld();
  if (!v && held.some((i) => same(i.p, entry.p))) return;
  const rest = held.filter((i) => !(same(i.p, entry.p) && ((i.v || null) === v || !i.v)));
  writeHeld([{ p: String(entry.p), h: entry.h, v, title: cut(entry.title || '', 120), price: Number(entry.price) || 0, at: Date.now() }, ...rest]);
}

/** Lets go of a held product the store no longer sells. */
function unhold(productId) {
  const held = readHeld();
  const rest = held.filter((i) => !same(i.p, productId));
  if (rest.length !== held.length) writeHeld(rest);
}

/** Returns and clears the held hearts (only once signed in). */
function takeHeld() {
  if (GUEST_LOCKED) return [];
  const held = readHeld();
  try {
    localStorage.removeItem(HELD_KEY);
  } catch {
    /* ignore */
  }
  return held;
}

function askToSignIn(pending, text = WORDS.signIn) {
  toast({
    text,
    link: { label: WORDS.signInLink, href: signInUrl(), onClick: () => setPending(pending) },
  });
}

/* Another tab changed the list (or, for a guest, what's held). */
window.addEventListener('storage', (event) => {
  if (event.key === HELD_KEY) return emit('held');
  if (event.key !== KEY) return;
  state = read();
  emit('sync');
});

/* Back/forward restores a page as it was left; the lists may have changed since. */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  state = read();
  emit('sync');
  pull();
});

/* ---------- data helpers ---------- */

const same = (a, b) => String(a) === String(b);
/* Cut to max characters without splitting an emoji (half of one is invalid text). */
const cut = (value, max) => String(value).slice(0, max).replace(/[\uD800-\uDBFF]$/, '');
const findList = (id) => state.lists.find((l) => l.id === id);

function ensureList(id, name) {
  let list = findList(id);
  if (!list) {
    list = { id, name, items: [] };
    if (id === LATER_ID) state.lists.splice(1, 0, list);
    else state.lists.push(list);
  }
  return list;
}

function savedProductIds() {
  const ids = new Set();
  state.lists.forEach((l) => l.items.forEach((i) => ids.add(String(i.p))));
  return ids;
}

function has(productId, listId) {
  const lists = listId ? [findList(listId)].filter(Boolean) : state.lists;
  return lists.some((l) => l.items.some((i) => same(i.p, productId)));
}

function count() {
  return savedProductIds().size;
}

/** Adds an entry {p, h, v, title, price} to a list. Returns false when it was already there.
    Stored as {p, h, v, n, pr, t}: product id, handle, variant id (null = no size
    chosen yet), name, price in cents when saved, time saved. */
function add(entry, listId = DEFAULT_ID) {
  const list = listId === LATER_ID ? ensureList(LATER_ID, 'Saved for later') : findList(listId);
  if (!list) return false;
  const v = entry.v ? String(entry.v) : null;
  if (list.items.some((i) => same(i.p, entry.p) && (i.v || null) === v)) return false;
  /* A product saved without a size is replaced by the sized entry. */
  if (v) list.items = list.items.filter((i) => !(same(i.p, entry.p) && !i.v));
  const item = { p: String(entry.p), h: entry.h, v, t: Date.now() };
  if (entry.title || entry.n) item.n = cut(entry.title || entry.n, 120);
  if (Number(entry.price || entry.pr)) item.pr = Number(entry.price || entry.pr);
  list.items.unshift(item);
  queueOp({ op: 'add', list: list.id, item: { ...item } });
  write('add', { entry, listId: list.id });
  return true;
}

/** Removes every entry of a product (from one list, or from all). Returns what it took out, for Undo. */
function removeProduct(productId, listId) {
  const removed = [];
  state.lists.forEach((l) => {
    if (listId && l.id !== listId) return;
    l.items.forEach((i, index) => {
      if (same(i.p, productId)) removed.push({ listId: l.id, item: i, index });
    });
    l.items = l.items.filter((i) => !same(i.p, productId));
  });
  if (removed.length) {
    queueOp({ op: 'remove', ...(listId ? { list: listId } : {}), p: String(productId) });
    write('remove', { productId, listId });
  }
  return removed;
}

function removeEntry(listId, productId, variantId) {
  const list = findList(listId);
  if (!list) return [];
  const v = variantId ? String(variantId) : null;
  const removed = [];
  list.items.forEach((i, index) => {
    if (same(i.p, productId) && (i.v || null) === v) removed.push({ listId, item: i, index });
  });
  list.items = list.items.filter((i) => !(same(i.p, productId) && (i.v || null) === v));
  if (removed.length) {
    queueOp({ op: 'remove', list: listId, p: String(productId), v });
    write('remove', { productId, listId });
  }
  return removed;
}

/** Puts back what removeProduct / removeEntry took out (the toast's Undo). */
function restore(removed) {
  removed.forEach(({ listId, item, index }) => {
    const list = listId === LATER_ID ? ensureList(LATER_ID, 'Saved for later') : findList(listId);
    if (!list || list.items.some((i) => same(i.p, item.p) && (i.v || null) === (item.v || null))) return;
    list.items.splice(Math.min(index, list.items.length), 0, item);
  });
  if (removed.length) {
    queueOp({ op: 'restore', entries: removed });
    write('restore');
  }
}

/** Empties a list. Returns what it took out, for Undo. */
function clearList(listId) {
  const list = findList(listId);
  if (!list || !list.items.length) return [];
  const removed = list.items.map((item, index) => ({ listId, item, index }));
  list.items = [];
  queueOp({ op: 'clear', list: listId });
  write('remove', { listId });
  return removed;
}

function setVariant(listId, productId, fromVariant, toVariant) {
  const list = findList(listId);
  if (!list) return;
  const from = fromVariant ? String(fromVariant) : null;
  const entry = list.items.find((i) => same(i.p, productId) && (i.v || null) === from);
  if (!entry || (entry.v || null) === (toVariant ? String(toVariant) : null)) return;
  entry.v = toVariant ? String(toVariant) : null;
  /* Two entries for the same size collapse into one. */
  list.items = list.items.filter((i, idx, all) => all.findIndex((j) => same(j.p, i.p) && (j.v || null) === (i.v || null)) === idx);
  queueOp({ op: 'variant', list: listId, p: String(productId), from, to: toVariant ? String(toVariant) : null });
  write('variant', { listId, productId });
}

function moveEntry(fromId, toId, productId, variantId) {
  if (fromId === toId) return;
  const removed = removeEntry(fromId, productId, variantId);
  removed.forEach(({ item }) => add({ ...item }, toId));
}

function createList(name) {
  const id = 'l' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  state.lists.push({ id, name: cut(name, 40), items: [] });
  queueOp({ op: 'createList', id, name: cut(name, 40) });
  write('list');
  return id;
}

function renameList(id, name) {
  const list = findList(id);
  if (!list || id === DEFAULT_ID || id === LATER_ID) return;
  list.name = cut(name, 40);
  queueOp({ op: 'renameList', id, name: list.name });
  write('list');
}

function deleteList(id) {
  if (id === DEFAULT_ID) return;
  state.lists = state.lists.filter((l) => l.id !== id);
  queueOp({ op: 'deleteList', id });
  write('list');
}

const lists = () => state.lists.map((l) => ({ id: l.id, name: l.name, items: l.items.map((i) => ({ ...i })) }));

/* ---------- events for marketing tools ---------- */

let klaviyoKnows = false;

function track(kind, info, variantId) {
  const price = Number(info.price || 0) / 100;
  const currency = window.Shopify?.currency?.active || config.currency || 'USD';
  const productId = String(info.p);
  const event = kind === 'later' ? 'Saved for Later' : 'Added to Wishlist';
  try {
    /* Klaviyo keeps events only for shoppers it already knows (from an email click or a
       form), so a signed-in shopper is named first, once per page. */
    if (MARKETING.klaviyo && CUSTOMER_EMAIL && !klaviyoKnows && window.klaviyo?.push) {
      window.klaviyo.push(['identify', { email: CUSTOMER_EMAIL }]);
      klaviyoKnows = true;
    }
    if (MARKETING.klaviyo) window.klaviyo?.push?.([
      'track',
      event,
      {
        ProductID: productId,
        ProductName: info.title,
        Brand: info.vendor,
        Price: price,
        VariantID: variantId ? String(variantId) : undefined,
        URL: `${location.origin}${config.root || '/'}products/${info.h}`,
      },
    ]);
  } catch {
    /* tracking must never break the heart */
  }
  try {
    if (MARKETING.ga4 && typeof window.gtag === 'function') {
      window.gtag('event', 'add_to_wishlist', {
        currency,
        value: price,
        items: [{ item_id: productId, item_name: info.title, item_brand: info.vendor, price, quantity: 1 }],
      });
    }
  } catch {
    /* ignore */
  }
  try {
    if (MARKETING.meta) window.fbq?.('track', 'AddToWishlist', { content_ids: [productId], content_name: info.title, content_type: 'product', value: price, currency });
  } catch {
    /* ignore */
  }
  try {
    if (MARKETING.shopify) window.Shopify?.analytics?.publish?.('hc_wishlist_add', { productId, variantId: variantId || null, title: info.title, vendor: info.vendor, price, list: kind });
  } catch {
    /* ignore */
  }
}

/* ---------- toast ---------- */

/* The message rides the top layer as a manual popover. While a modal window is
   open (quick add, a drawer) everything outside it is inert, so the message is
   moved inside that window: it stays clickable and, being in the top layer,
   still sits at the bottom of the screen rather than inside the window. */
const POPOVER = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;
let toastEl = null;
let toastTimer = 0;
/* Where the message shows, how it looks and how long it stays (Apps > HC Wishlist >
   Store hearts; checked in hc-globals, checked again here). */
const TOAST_POSITIONS = ['bottom', 'bottom-left', 'bottom-right', 'top'];
const TOAST = {
  position: TOAST_POSITIONS.includes(config.toast?.position) ? config.toast.position : 'bottom',
  light: config.toast?.style === 'light',
  time: Math.min(15000, Math.max(2000, Number(config.toast?.time) || 5000)),
};
let toastHost = null;

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function topModal() {
  try {
    const open = [...document.querySelectorAll('dialog[open]')].filter((d) => d.matches(':modal'));
    return open[open.length - 1] || null;
  } catch {
    return null;
  }
}

function hideToast() {
  if (!toastEl) return;
  clearTimeout(toastTimer);
  if (POPOVER) {
    if (toastEl.matches(':popover-open')) toastEl.hidePopover();
  } else {
    toastEl.hidden = true;
  }
}

function toast({ text, link, action }) {
  if (!toastEl) {
    toastEl = el('div', 'hc-wl-toast');
    if (TOAST.position !== 'bottom') toastEl.classList.add(`hc-wl-toast--${TOAST.position}`);
    if (TOAST.light) toastEl.classList.add('hc-wl-toast--light');
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    if (POPOVER) toastEl.popover = 'manual';
    else toastEl.hidden = true;
    toastEl.addEventListener('pointerenter', () => clearTimeout(toastTimer));
    toastEl.addEventListener('pointerleave', () => {
      toastTimer = setTimeout(hideToast, Math.min(3000, TOAST.time));
    });
  }
  hideToast();
  const host = topModal() || document.body;
  if (toastEl.parentElement !== host) host.append(toastEl);
  if (host !== toastHost) {
    toastHost = host;
    if (host !== document.body) host.addEventListener('close', hideToast, { once: true });
  }
  toastEl.replaceChildren(el('span', 'hc-wl-toast__text', text));
  if (link) {
    const a = el('a', 'hc-wl-toast__link', link.label);
    a.href = link.href;
    if (link.onClick) a.addEventListener('click', link.onClick);
    toastEl.append(a);
  }
  if (action) {
    const b = el('button', 'hc-wl-toast__link hc-wl-toast__action', action.label);
    b.type = 'button';
    b.addEventListener('click', () => {
      hideToast();
      action.run();
    });
    toastEl.append(b);
  }
  const close = el('button', 'hc-wl-toast__close');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.addEventListener('click', hideToast);
  toastEl.append(close);
  if (POPOVER) toastEl.showPopover();
  else toastEl.hidden = false;
  toastTimer = setTimeout(hideToast, TOAST.time);
}

/* ---------- list picker ---------- */

let picker = null;
let pickerEntry = null;

function renderPicker() {
  if (!picker || !pickerEntry) return;
  const entry = pickerEntry;
  const focusedId = document.activeElement?.dataset?.listId;
  const body = el('div', 'hc-wl-picker__body');
  const close = el('button', 'hc-wl-picker__close');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.addEventListener('click', () => picker.close());
  body.append(close, el('h2', 'hc-wl-picker__title', 'Save to a list'));
  if (entry.title) body.append(el('p', 'hc-wl-picker__product', entry.title));

  const ul = el('ul', 'hc-wl-picker__lists');
  ul.setAttribute('role', 'list');
  state.lists.forEach((list) => {
    /* "Saved for later" belongs to the cart; it shows here only to take an item out. */
    if (list.id === LATER_ID && !list.items.some((i) => same(i.p, entry.p))) return;
    const li = el('li');
    const label = el('label', 'hc-wl-picker__row');
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.dataset.listId = list.id;
    box.checked = list.items.some((i) => same(i.p, entry.p));
    box.addEventListener('change', () => {
      if (box.checked) add(entry, list.id);
      else removeProduct(entry.p, list.id);
    });
    label.append(box, el('span', 'hc-wl-picker__name', list.name), el('span', 'hc-wl-picker__count', String(list.items.length)));
    li.append(label);
    ul.append(li);
  });
  body.append(ul);

  if (LISTS_ON) {
    const form = el('form', 'hc-wl-picker__new');
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 40;
    input.required = true;
    input.placeholder = 'New list name';
    input.setAttribute('aria-label', 'New list name');
    input.className = 'hc-wl-picker__input';
    const create = el('button', 'hc-wl-picker__create', 'Create');
    create.type = 'submit';
    form.append(input, create);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = input.value.trim();
      if (!name) return;
      add(entry, createList(name));
    });
    body.append(form);
  }

  const done = el('button', 'button hc-wl-picker__done', 'Done');
  done.type = 'button';
  done.addEventListener('click', () => picker.close());
  body.append(done);
  picker.replaceChildren(body);
  if (focusedId) picker.querySelector(`[data-list-id="${focusedId}"]`)?.focus();
}

function openPicker(entry) {
  if (!picker) {
    picker = el('dialog', 'hc-wl-picker');
    picker.setAttribute('aria-label', 'Save to a list');
    picker.addEventListener('click', (event) => {
      if (event.target === picker) picker.close();
    });
    picker.addEventListener('close', () => {
      pickerEntry = null;
    });
    document.body.append(picker);
  }
  pickerEntry = entry;
  renderPicker();
  hideToast();
  picker.showModal();
}

/* ---------- hearts, header count, save for later ---------- */

function info(node) {
  return {
    p: node.dataset.productId,
    h: node.dataset.handle,
    title: node.dataset.title || '',
    vendor: node.dataset.vendor || '',
    price: node.dataset.price || 0,
  };
}

function syncHearts() {
  const saved = savedProductIds();
  document.querySelectorAll('[data-hc-wl-heart]').forEach((btn) => {
    const on = saved.has(String(btn.dataset.productId));
    if (btn.getAttribute('aria-pressed') !== String(on)) btn.setAttribute('aria-pressed', String(on));
  });
}

function syncCount() {
  const n = count();
  document.querySelectorAll('[data-hc-wl-count]').forEach((node) => {
    node.textContent = n > 99 ? '99+' : String(n);
  });
  document.querySelectorAll('[data-hc-wl-count-wrap]').forEach((node) => node.classList.toggle('visually-hidden', n === 0));
  document.querySelectorAll('[data-hc-wl-header]').forEach((link) => {
    link.classList.toggle('hc-wl-header--has-items', n > 0);
    link.setAttribute('aria-label', n ? `Wishlist, ${n} item${n === 1 ? '' : 's'}` : 'Wishlist');
  });
}

function selectedVariant(btn) {
  const scope = btn.closest('product-form-component') || btn.closest('form');
  return scope?.querySelector('input[name="id"]')?.value || null;
}

function onHeart(btn) {
  const item = info(btn);
  if (GUEST_LOCKED) {
    const v = btn.classList.contains('hc-wl-heart--pdp') ? selectedVariant(btn) : null;
    const entry = { p: item.p, h: item.h, v, title: item.title, price: item.price };
    hold(entry);
    askToSignIn({ kind: 'heart', entry, info: item });
    return;
  }
  if (has(item.p)) {
    const removed = removeProduct(item.p);
    toast({
      text: removed.length > 1 ? WORDS.removedLists : WORDS.removed,
      action: { label: WORDS.undo, run: () => restore(removed) },
    });
    return;
  }
  const v = btn.classList.contains('hc-wl-heart--pdp') ? selectedVariant(btn) : null;
  const entry = { p: item.p, h: item.h, v, title: item.title, price: item.price };
  add(entry, DEFAULT_ID);
  track('add', item, v);
  toast({
    text: WORDS.saved,
    link: { label: WORDS.view, href: PAGE_URL },
    action: LISTS_ON ? { label: WORDS.changeList, run: () => openPicker(entry) } : null,
  });
}

function onSaveLater(btn) {
  const item = info(btn);
  const v = btn.dataset.variantId || null;
  if (GUEST_LOCKED) {
    askToSignIn({ kind: 'later', entry: { p: item.p, h: item.h, v, title: item.title, price: item.price }, info: item }, WORDS.signInLater);
    return;
  }
  add({ p: item.p, h: item.h, v, title: item.title, price: item.price }, LATER_ID);
  track('later', item, v);
  /* The cart's own remove button does the rest (totals, drawer, bubble). */
  btn.closest('tr')?.querySelector('.cart-items__remove')?.click();
  toast({ text: WORDS.savedLater, link: { label: WORDS.view, href: PAGE_URL === STORE_PAGE_URL ? `${PAGE_URL}#${LATER_ID}` : PAGE_URL } });
}

/* Capture phase: a card heart must not reach the card's own click handling. */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const heart = target?.closest('[data-hc-wl-heart]');
    if (heart) {
      event.preventDefault();
      event.stopPropagation();
      onHeart(heart);
      return;
    }
    const later = target?.closest('[data-hc-wl-save-later]');
    if (later) {
      event.preventDefault();
      onSaveLater(later);
    }
  },
  true
);

/* Cards arrive later too (infinite scroll, carousels, recommendations, quick
   add), and a variant change morphs the buy row back to the server's
   aria-pressed="false". Both are put right before the next paint. */
const WATCHED = '[data-hc-wl-heart], [data-hc-wl-count]';
const observer = new MutationObserver((records) => {
  for (const record of records) {
    const touched =
      record.type === 'attributes'
        ? record.target.matches?.('[data-hc-wl-heart]')
        : [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches(WATCHED) || node.querySelector(WATCHED)));
    if (touched) {
      syncHearts();
      syncCount();
      return;
    }
  }
});

document.addEventListener('hc:wishlist:change', () => {
  syncHearts();
  syncCount();
  if (picker?.open) renderPicker();
});

const api = {
  DEFAULT_ID,
  LATER_ID,
  pageUrl: PAGE_URL,
  storePageUrl: STORE_PAGE_URL,
  signedIn: !!CUSTOMER,
  synced: SYNC,
  refresh: () => pull(true),
  guestLocked: GUEST_LOCKED,
  guestPage: GUEST_PAGE,
  signInUrl,
  askToSignIn,
  takePending,
  /* The guest page's items: what a guest tapped in the last day. */
  held: () => (GUEST_LOCKED ? readHeld() : []),
  unhold,
  /* Sends waiting changes now (before the page goes somewhere else). */
  flushNow: () => {
    clearTimeout(flushTimer);
    return flush();
  },
  listsEnabled: LISTS_ON,
  shareEnabled: config.share !== false,
  root: config.root || '/',
  lists,
  has,
  count,
  add,
  removeProduct,
  removeEntry,
  clearList,
  restore,
  setVariant,
  moveEntry,
  createList,
  renameList,
  deleteList,
  openPicker,
  toast,
  track,
  words: WORDS,
};

window.HCWishlist = api;
syncHearts();
syncCount();

/* Back from signing in: save what the shopper tapped as a guest - the heart they signed
   in from, and (guest page on) every heart held in the last day. */
(() => {
  const pending = takePending('heart') || takePending('later');
  const held = takeHeld();
  if (pending?.kind === 'later' && pending.entry?.p) {
    const line = document.querySelector(`[data-hc-wl-save-later][data-variant-id="${CSS.escape(String(pending.entry.v || ''))}"]`);
    if (line) onSaveLater(line);
    else {
      add(pending.entry, LATER_ID);
      if (!held.length) toast({ text: WORDS.savedLater, link: { label: WORDS.view, href: PAGE_URL } });
    }
  }
  const hearts = [...(pending?.kind === 'heart' && pending.entry?.p ? [{ entry: pending.entry, info: pending.info }] : []), ...held.map((entry) => ({ entry, info: entry }))];
  if (!hearts.length) return;
  /* Oldest first, so the newest tap ends up at the top of the list. */
  for (const { entry, info } of hearts.reverse()) {
    if (has(entry.p) && !entry.v) continue;
    if (add(entry, DEFAULT_ID)) track('add', { ...info, ...entry }, entry.v);
  }
  toast({ text: WORDS.saved, link: { label: WORDS.view, href: PAGE_URL } });
})();

/* Move to cart from the account's Wishlist page: the account adds the item through
   /cart/add and comes back to /cart?hc_wl_moved=<product>-<variant>; taking it off
   Saved for later here finishes the move. */
(() => {
  const params = new URLSearchParams(location.search);
  const moved = params.get('hc_wl_moved');
  if (moved == null) return;
  params.delete('hc_wl_moved');
  const query = params.toString();
  history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
  const [p, v] = moved.split('-');
  if (!SYNC || !/^\d{1,20}$/.test(p || '') || !/^\d{1,20}$/.test(v || '')) return;
  /* Only a real move: the item must be in the cart, so a link from somewhere else can't
     simply take things off Saved for later. */
  fetch(`${config.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((res) => res.json())
    .then((cart) => {
      if (!cart?.items?.some((line) => String(line.variant_id) === v)) return;
      /* Sent even when this browser's copy is behind, so the account's copy loses it too. */
      if (!removeEntry(LATER_ID, p, v).length) queueOp({ op: 'remove', list: LATER_ID, p, v });
      toast({ text: WORDS.moved });
    })
    .catch(() => {});
})();
observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'data-product-id'] });
document.dispatchEvent(new CustomEvent('hc:wishlist:ready'));
pull();
